import { useSegment } from '@/modules/analytics'
import { useModal } from '@/utils'
import React, { ReactNode, useContext } from 'react'
import { ContactModal } from '../components/molecules/ContactModal/Component'

interface ContactModalConfig {
  hasHeadquarters: boolean
  hide: {
    chat?: boolean
    header?: boolean
  }
}

interface ContactModalContextType {
  openContact: () => void
  closeContact: () => void
}

const defaultConfig: ContactModalConfig = {
  hasHeadquarters: false,
  hide: {},
}

export const ModalContext = React.createContext<
  ContactModalContextType | undefined
>(undefined)

interface ContactModalProviderProps {
  children: ReactNode
  config?: ContactModalConfig
}

export const ContactModalProvider = ({
  children,
  config = defaultConfig,
}: ContactModalProviderProps) => {
  const { open: openContact, close: closeContact, isOpen } = useModal()
  const analytics = useSegment()

  const trackAndOpenContact = () => {
    analytics.track('Contact Modal Open')
    openContact()
  }

  return (
    <ModalContext.Provider
      value={{ openContact: trackAndOpenContact, closeContact }}
    >
      {children}
      {isOpen && (
        <ContactModal
          hasHeadquarters={config.hasHeadquarters}
          isOpen={isOpen}
          onClose={closeContact}
        />
      )}
    </ModalContext.Provider>
  )
}

export const useContactModal = () => {
  const context = useContext(ModalContext)
  if (context === undefined) {
    throw new Error(
      'useContactModal must be used within a ContactModalProvider',
    )
  }
  return context
}
